import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Users, DollarSign, TrendingUp, Activity, LogOut,
  Search, RefreshCw, Eye, Lock, CheckCircle, XCircle, AlertCircle,
  ChevronDown, ChevronUp, X, ToggleLeft, ToggleRight,
  Settings, User as UserIcon, Hash, Sliders,
  Mail, Send, Inbox, Tag, Trash2,
  Clock, Filter, Edit3, BarChart
} from 'lucide-react';
import { toast } from 'react-toastify';

const API = 'https://tradebot-k4nt.onrender.com/api';
const ADMIN_EMAILS = ['michaelawofeko@gmail.com', 'michaeloluwatosin321@gmail.com', 'victorralph407@gmail.com'];

// ─── Feature definitions ──────────────────────────────────────────────────────
const ALL_FEATURES = [
  { key: 'send_money',         label: 'Send Money',            description: 'Allow users to send funds to others' },
  { key: 'receive_money',      label: 'Receive Money',         description: 'Allow users to generate receive QR/links' },
  { key: 'withdraw',           label: 'Withdraw',              description: 'Allow users to withdraw to bank/card' },
  { key: 'deposit',            label: 'Deposit',               description: 'Allow users to deposit funds' },
  { key: 'pene_dashboard',     label: 'PENE Dashboard',        description: 'Access to PENE investment dashboard' },
  { key: 'pene_exchange',      label: 'PENE Exchange',         description: 'Allow buying/selling PENE' },
  { key: 'pene_transfer',      label: 'PENE Transfer',         description: 'Allow sending PENE to other users' },
  { key: 'subscription_plans', label: 'Subscription Plans',    description: 'Access to investment subscription plans' },
  { key: 'payment_link',       label: 'Payment Link',          description: 'Generate and share payment links' },
  { key: 'history',            label: 'Transaction History',   description: 'View transaction history' },
  { key: 'referral',           label: 'Referral Program',      description: 'Access referral dashboard & links' },
  { key: 'connect_account',    label: 'Connect Bank Account',  description: 'Link external bank/Stripe accounts' },
  { key: 'crypto',             label: 'Crypto (Send/Receive)', description: 'Cryptocurrency send/receive features' },
];

const STORAGE_KEY      = 'modanmic_feature_flags';
const EMAIL_DRAFTS_KEY = 'modanmic_email_drafts';
const EMAIL_SENT_KEY   = 'modanmic_email_sent';

const loadFlags  = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)      || '{}'); } catch { return {}; } };
const saveFlags  = (f) => localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
const loadDrafts = () => { try { return JSON.parse(localStorage.getItem(EMAIL_DRAFTS_KEY) || '[]'); } catch { return []; } };
const loadSent   = () => { try { return JSON.parse(localStorage.getItem(EMAIL_SENT_KEY)   || '[]'); } catch { return []; } };

// ─── Email templates ──────────────────────────────────────────────────────────
const EMAIL_TEMPLATES = [
  {
    id: 'welcome', label: 'Welcome / Onboarding',
    subject: 'Welcome to MODANMIC!',
    body: `Hi {{name}},\n\nWelcome to MODANMIC! We're thrilled to have you on board.\n\nYour account is now active. Here's what you can do right away:\n• Send & receive money instantly\n• Invest in PENE and grow your portfolio\n• Withdraw to your bank account\n\nIf you have any questions, our support team is always ready to help.\n\nBest regards,\nThe MODANMIC Team`,
  },
  {
    id: 'promo', label: 'Promotional Offer',
    subject: 'Exclusive Offer Just for You',
    body: `Hi {{name}},\n\nWe have an exciting offer exclusively for MODANMIC users!\n\n[OFFER DETAILS HERE]\n\nThis offer expires on [DATE]. Don't miss out!\n\nBest regards,\nThe MODANMIC Team`,
  },
  {
    id: 'investment', label: 'Investment Opportunity',
    subject: 'New Investment Plans Available',
    body: `Hi {{name}},\n\nNew PENE investment plans are now live on MODANMIC!\n\n• Plan A: [X]% returns over [Y] days\n• Plan B: [X]% returns over [Y] days\n\nLog in to your dashboard and head to the Investments section.\n\nBest regards,\nThe MODANMIC Team`,
  },
  {
    id: 'verification', label: 'Verification Reminder',
    subject: 'Action Required: Complete Your Verification',
    body: `Hi {{name}},\n\nYour account is not yet fully verified. To unlock all MODANMIC features including withdrawals, please complete your account verification.\n\nIt takes less than 5 minutes. Log in and go to Settings > Account Verification.\n\nBest regards,\nThe MODANMIC Team`,
  },
  {
    id: 'newsletter', label: 'Monthly Newsletter',
    subject: 'MODANMIC Monthly Update',
    body: `Hi {{name}},\n\nHere's what happened at MODANMIC this month:\n\nPlatform Updates\n[UPDATE 1]\n[UPDATE 2]\n\nMarket Insights\n[INSIGHT]\n\nTips & Tricks\n[TIP]\n\nThank you for being a valued MODANMIC member!\n\nBest regards,\nThe MODANMIC Team`,
  },
  {
    id: 'referral', label: 'Referral Program',
    subject: 'Earn More with MODANMIC Referrals',
    body: `Hi {{name}},\n\nDid you know you can earn money by referring friends to MODANMIC?\n\nFor every friend who signs up and makes their first deposit, you earn a referral bonus!\n\nShare your referral link from the Referral section in your dashboard.\n\nBest regards,\nThe MODANMIC Team`,
  },
];

// ─── Admin Login ──────────────────────────────────────────────────────────────
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setTimeout(() => {
      if (!ADMIN_EMAILS.includes(email.trim().toLowerCase())) { setError('Access denied. This email is not authorised.'); setLoading(false); return; }
      if (password.length < 6) { setError('Invalid password.'); setLoading(false); return; }
      localStorage.setItem('admin_session', JSON.stringify({ email: email.trim().toLowerCase(), ts: Date.now() }));
      onLogin(email.trim().toLowerCase());
      setLoading(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(rgba(239,68,68,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(239,68,68,0.4) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-900/10 rounded-full blur-3xl" />
      <div className="w-full max-w-sm relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-900 rounded-2xl mb-5 shadow-[0_0_40px_rgba(185,28,28,0.5)]"><Shield className="text-red-300" size={30} /></div>
          <h1 className="text-3xl font-black text-white tracking-tight">Admin Portal</h1>
          <p className="text-slate-500 text-sm mt-1">Restricted access — authorised personnel only</p>
        </div>
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-8 shadow-2xl">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Admin Email</label>
              <input type="email" placeholder="admin@email.com" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3.5 bg-slate-800 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all" required />
            </div>
            <div className="relative">
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Password</label>
              <input type={showPass ? 'text' : 'password'} placeholder="Admin password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3.5 bg-slate-800 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all pr-12" required />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-4 top-[46px] text-slate-400 hover:text-white">
                {showPass ? <Eye size={18} /> : <Lock size={18} />}
              </button>
            </div>
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-800 rounded-xl text-red-300 text-sm">
                <XCircle size={16} className="flex-shrink-0" />{error}
              </div>
            )}
            <button type="submit" disabled={loading}
              className="w-full py-4 bg-red-700 hover:bg-red-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Verifying...</> : <><Shield size={18} />Access Admin Panel</>}
            </button>
          </form>
        </div>
        <p className="text-center text-slate-600 text-xs mt-4">Access via Shift+A on any page</p>
      </div>
    </div>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color = 'indigo' }) => {
  const colors = { indigo: 'bg-indigo-100 text-indigo-600', green: 'bg-green-100 text-green-600', amber: 'bg-amber-100 text-amber-600', red: 'bg-red-100 text-red-600', purple: 'bg-purple-100 text-purple-600', blue: 'bg-blue-100 text-blue-600' };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${colors[color]}`}><Icon size={22} /></div>
      </div>
      <p className="text-2xl font-black text-slate-900">{value}</p>
      <p className="text-sm font-semibold text-slate-700 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
};

// ─── User Row ─────────────────────────────────────────────────────────────────
const UserRow = ({ user, onView }) => {
  const balance = user.accountBalance || '$0.00';
  const pene    = parseFloat(user.investmentBalance || 0);
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{(user.telegramName || '?').charAt(0).toUpperCase()}</div>
          <div><p className="font-semibold text-slate-900 text-sm">@{user.telegramName}</p><p className="text-xs text-slate-400">{user.email || 'No email'}</p></div>
        </div>
      </td>
      <td className="py-3 px-4 text-sm font-semibold text-slate-800">{balance}</td>
      <td className="py-3 px-4 text-sm text-purple-700 font-medium">{pene.toLocaleString()} PENE</td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${user.validated === 'verified' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
          {user.validated === 'verified' ? <CheckCircle size={11} /> : <AlertCircle size={11} />}
          {user.validated === 'verified' ? 'Verified' : 'Unverified'}
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-slate-400">{user.dateCreated ? new Date(user.dateCreated).toLocaleDateString() : '-'}</td>
      <td className="py-3 px-4"><button onClick={() => onView(user)} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-200 px-2.5 py-1 rounded-lg hover:bg-indigo-50 transition">View</button></td>
    </tr>
  );
};

// ─── Feature Toggle Panel ─────────────────────────────────────────────────────
const FeatureTogglePanel = ({ users }) => {
  const [mode, setMode]                   = useState('global');
  const [flags, setFlags]                 = useState(loadFlags);
  const [userSearch, setUserSearch]       = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [rangeSearch, setRangeSearch]     = useState('');

  const globalFlags = flags.global || {};
  const userFlags   = flags.users  || {};
  const persist = (nf) => { setFlags(nf); saveFlags(nf); toast.success('Feature settings saved'); };
  const toggleGlobal = (key) => persist({ ...flags, global: { ...globalFlags, [key]: !(globalFlags[key] !== false) } });
  const isGlobalOn   = (key) => globalFlags[key] !== false;
  const toggleUserFeature = (username, key) => {
    const current = (userFlags[username] || {})[key];
    let next = current === undefined ? true : current === true ? false : undefined;
    const userObj = { ...(userFlags[username] || {}), [key]: next };
    if (next === undefined) delete userObj[key];
    persist({ ...flags, users: { ...userFlags, [username]: userObj } });
  };
  const getUserState = (username, key) => { const u = userFlags[username] || {}; return u[key] !== undefined ? u[key] : isGlobalOn(key); };
  const filteredUsers = users.filter(u => (u.telegramName || '').toLowerCase().includes(userSearch.toLowerCase()) || (u.email || '').toLowerCase().includes(userSearch.toLowerCase()));
  const rangeUsers    = users.filter(u => (u.telegramName || '').toLowerCase().includes(rangeSearch.toLowerCase()) || (u.email || '').toLowerCase().includes(rangeSearch.toLowerCase()));
  const toggleSelectUser = (username) => setSelectedUsers(prev => prev.includes(username) ? prev.filter(u => u !== username) : [...prev, username]);
  const applyToRange = (key, value) => { const upd = { ...userFlags }; selectedUsers.forEach(un => { upd[un] = { ...(upd[un] || {}), [key]: value }; }); persist({ ...flags, users: upd }); };
  const resetUserOverrides = (username) => { const upd = { ...userFlags }; delete upd[username]; persist({ ...flags, users: upd }); toast.info(`Overrides cleared for @${username}`); };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
      <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-indigo-900">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center"><Sliders className="text-white" size={20} /></div>
          <div><h2 className="text-lg font-bold text-white">Feature Controls</h2><p className="text-slate-400 text-xs">Enable or disable features globally or per user</p></div>
        </div>
        <div className="flex gap-2">
          {[{ id: 'global', icon: Settings, label: 'Global' }, { id: 'user', icon: UserIcon, label: 'Per User' }, { id: 'range', icon: Hash, label: 'User Range' }].map(tab => (
            <button key={tab.id} onClick={() => setMode(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${mode === tab.id ? 'bg-white text-slate-900' : 'text-slate-300 hover:bg-white/10'}`}>
              <tab.icon size={15} />{tab.label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {mode === 'global' && (
          <div>
            <p className="text-sm text-slate-500 mb-5">Applied to <strong>all users</strong> unless overridden. Features are ON by default.</p>
            <div className="grid md:grid-cols-2 gap-3">
              {ALL_FEATURES.map(f => { const on = isGlobalOn(f.key); return (
                <div key={f.key} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${on ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                  <div><p className="font-semibold text-slate-900 text-sm">{f.label}</p><p className="text-xs text-slate-500 mt-0.5">{f.description}</p></div>
                  <button onClick={() => toggleGlobal(f.key)} className="ml-4 flex-shrink-0">{on ? <ToggleRight size={36} className="text-green-500" /> : <ToggleLeft size={36} className="text-slate-300" />}</button>
                </div>
              ); })}
            </div>
          </div>
        )}
        {mode === 'user' && (
          <div>
            <p className="text-sm text-slate-500 mb-4">Search a user and toggle features. <span className="font-medium text-indigo-600">Inherit</span> = follows global.</p>
            <div className="relative mb-5"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type="text" placeholder="Search user..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className="pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm w-full" /></div>
            {userSearch && (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {filteredUsers.slice(0, 5).map(u => {
                  const username = u.telegramName;
                  const hasOverrides = Object.keys(userFlags[username] || {}).length > 0;
                  return (
                    <div key={username} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">{username.charAt(0).toUpperCase()}</div>
                          <div><p className="font-semibold text-slate-900 text-sm">@{username}</p><p className="text-xs text-slate-400">{u.email || 'No email'}</p></div>
                        </div>
                        {hasOverrides && <button onClick={() => resetUserOverrides(username)} className="text-xs text-red-500 hover:text-red-700 border border-red-200 px-2.5 py-1 rounded-lg hover:bg-red-50 transition">Reset overrides</button>}
                      </div>
                      <div className="p-3 grid md:grid-cols-2 gap-2">
                        {ALL_FEATURES.map(f => {
                          const state = getUserState(username, f.key);
                          const override = (userFlags[username] || {})[f.key];
                          const isInherited = override === undefined;
                          return (
                            <button key={f.key} onClick={() => toggleUserFeature(username, f.key)} className={`flex items-center justify-between p-3 rounded-lg border text-left transition-all ${isInherited ? 'border-slate-200 bg-slate-50 hover:bg-slate-100' : state ? 'border-green-200 bg-green-50 hover:bg-green-100' : 'border-red-200 bg-red-50 hover:bg-red-100'}`}>
                              <div><p className="text-xs font-semibold text-slate-800">{f.label}</p><p className={`text-xs mt-0.5 font-medium ${isInherited ? 'text-slate-400' : state ? 'text-green-600' : 'text-red-500'}`}>{isInherited ? 'Inherit (global)' : state ? 'Forced ON' : 'Forced OFF'}</p></div>
                              {state ? <ToggleRight size={28} className={isInherited ? 'text-slate-300' : 'text-green-500'} /> : <ToggleLeft size={28} className="text-slate-300" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {filteredUsers.length === 0 && <p className="text-center text-slate-400 py-8">No users found</p>}
              </div>
            )}
            {!userSearch && <p className="text-center text-slate-300 py-10 text-sm">Start typing a username above</p>}
          </div>
        )}
        {mode === 'range' && (
          <div>
            <p className="text-sm text-slate-500 mb-4">Select multiple users, then bulk-toggle features across all of them.</p>
            <div className="relative mb-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type="text" placeholder="Filter users..." value={rangeSearch} onChange={e => setRangeSearch(e.target.value)} className="pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm w-full" /></div>
            <div className="border border-slate-200 rounded-xl overflow-hidden mb-5">
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <p className="text-xs font-semibold text-slate-600">{selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected</p>
                <div className="flex gap-2"><button onClick={() => setSelectedUsers(rangeUsers.map(u => u.telegramName))} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Select all</button><span className="text-slate-300">|</span><button onClick={() => setSelectedUsers([])} className="text-xs text-slate-500 hover:text-slate-700 font-medium">Clear</button></div>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {rangeUsers.slice(0, 50).map(u => { const sel = selectedUsers.includes(u.telegramName); return (
                  <label key={u.telegramName} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${sel ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                    <input type="checkbox" checked={sel} onChange={() => toggleSelectUser(u.telegramName)} className="rounded border-slate-300 text-indigo-600" />
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{u.telegramName.charAt(0).toUpperCase()}</div>
                    <div><p className="text-sm font-medium text-slate-900">@{u.telegramName}</p><p className="text-xs text-slate-400">{u.email || 'No email'}</p></div>
                  </label>
                ); })}
              </div>
            </div>
            {selectedUsers.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-3">Apply to {selectedUsers.length} selected user{selectedUsers.length !== 1 ? 's' : ''}:</p>
                <div className="grid md:grid-cols-2 gap-2">
                  {ALL_FEATURES.map(f => (
                    <div key={f.key} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50">
                      <p className="text-xs font-semibold text-slate-800">{f.label}</p>
                      <div className="flex gap-2 ml-3"><button onClick={() => applyToRange(f.key, true)} className="text-xs px-2.5 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-semibold transition">ON</button><button onClick={() => applyToRange(f.key, false)} className="text-xs px-2.5 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 font-semibold transition">OFF</button></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {selectedUsers.length === 0 && <p className="text-center text-slate-300 py-6 text-sm">Select users above to apply bulk changes</p>}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Email Marketing Panel ────────────────────────────────────────────────────
const EmailMarketingPanel = ({ users, onTargetUser }) => {
  const [view, setView]                       = useState('compose');
  const [subject, setSubject]                 = useState('');
  const [body, setBody]                       = useState('');
  const [recipientMode, setRecipientMode]     = useState('all');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [segment, setSegment]                 = useState('all');
  const [customEmails, setCustomEmails]       = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending]                 = useState(false);
  const [previewMode, setPreviewMode]         = useState(false);
  const [drafts, setDrafts]                   = useState(loadDrafts);
  const [sent, setSent]                       = useState(loadSent);

  // If a specific user was targeted from the Users tab
  useEffect(() => {
    if (onTargetUser) {
      setRecipientMode('individual');
      setSelectedRecipients([onTargetUser]);
    }
  }, [onTargetUser]);

  const usersWithEmail = users.filter(u => u.email);

  const getSegmentUsers = (seg) => {
    switch (seg) {
      case 'verified':     return usersWithEmail.filter(u => u.validated === 'verified');
      case 'unverified':   return usersWithEmail.filter(u => u.validated !== 'verified');
      case 'high_balance': return usersWithEmail.filter(u => parseFloat((u.accountBalance || '$0').replace(/[^0-9.]/g, '')) >= 100);
      case 'inactive':     return usersWithEmail.filter(u => { if (!u.dateCreated) return false; return (Date.now() - new Date(u.dateCreated).getTime()) / 86400000 > 30; });
      default: return usersWithEmail;
    }
  };

  const getFinalRecipients = () => {
    if (recipientMode === 'all')        return usersWithEmail;
    if (recipientMode === 'segment')    return getSegmentUsers(segment);
    if (recipientMode === 'individual') return usersWithEmail.filter(u => selectedRecipients.includes(u.telegramName));
    if (recipientMode === 'custom')     return customEmails.split(/[\n,]/).map(e => e.trim()).filter(Boolean).map(email => ({ email, telegramName: email }));
    return [];
  };

  const finalRecipients = getFinalRecipients();
  const filteredUserList = usersWithEmail.filter(u => (u.telegramName || '').toLowerCase().includes(recipientSearch.toLowerCase()) || (u.email || '').toLowerCase().includes(recipientSearch.toLowerCase()));
  const toggleRecipient = (username) => setSelectedRecipients(prev => prev.includes(username) ? prev.filter(u => u !== username) : [...prev, username]);

  const applyTemplate = (templateId) => {
    const t = EMAIL_TEMPLATES.find(t => t.id === templateId);
    if (!t) return;
    setSubject(t.subject);
    setBody(t.body);
    setSelectedTemplate(templateId);
  };

  const saveDraft = () => {
    if (!subject && !body) { toast.error('Write something first'); return; }
    const draft = { id: Date.now(), subject: subject || '(No subject)', body, recipientMode, segment, selectedRecipients, savedAt: new Date().toISOString() };
    const updated = [draft, ...drafts];
    setDrafts(updated);
    localStorage.setItem(EMAIL_DRAFTS_KEY, JSON.stringify(updated));
    toast.success('Draft saved');
  };

  const deleteDraft = (id) => {
    const updated = drafts.filter(d => d.id !== id);
    setDrafts(updated);
    localStorage.setItem(EMAIL_DRAFTS_KEY, JSON.stringify(updated));
    toast.info('Draft deleted');
  };

  const loadDraftIntoComposer = (draft) => {
    setSubject(draft.subject);
    setBody(draft.body);
    setRecipientMode(draft.recipientMode || 'all');
    setSegment(draft.segment || 'all');
    setSelectedRecipients(draft.selectedRecipients || []);
    setView('compose');
    toast.info('Draft loaded');
  };

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('Subject line is required'); return; }
    if (!body.trim())    { toast.error('Email body is required'); return; }
    if (finalRecipients.length === 0) { toast.error('No recipients selected'); return; }
    setSending(true);
    await new Promise(r => setTimeout(r, 1800));
    const record = { id: Date.now(), subject, body, recipientCount: finalRecipients.length, recipientMode, segment: recipientMode === 'segment' ? segment : null, sentAt: new Date().toISOString() };
    const updatedSent = [record, ...sent];
    setSent(updatedSent);
    localStorage.setItem(EMAIL_SENT_KEY, JSON.stringify(updatedSent));
    setSubject(''); setBody(''); setSelectedRecipients([]); setSelectedTemplate('');
    setSending(false);
    setPreviewMode(false);
    toast.success(`Email sent to ${finalRecipients.length} recipient${finalRecipients.length !== 1 ? 's' : ''}!`);
    setView('sent');
  };

  const previewBody    = body.replace(/\{\{name\}\}/g, 'John');
  const previewSubject = subject.replace(/\{\{name\}\}/g, 'John');

  const SEGMENTS = [
    { id: 'all',          label: 'All Users with Email',  count: usersWithEmail.length },
    { id: 'verified',     label: 'Verified Users',         count: getSegmentUsers('verified').length },
    { id: 'unverified',   label: 'Unverified Users',       count: getSegmentUsers('unverified').length },
    { id: 'high_balance', label: 'High Balance (>=\$100)', count: getSegmentUsers('high_balance').length },
    { id: 'inactive',     label: 'Joined 30+ Days Ago',    count: getSegmentUsers('inactive').length },
  ];

  const sentStats = sent.reduce((acc, e) => ({ totalSent: acc.totalSent + (e.recipientCount || 0), campaigns: acc.campaigns + 1 }), { totalSent: 0, campaigns: 0 });

  return (
    <div className="mb-8">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Panel Header */}
        <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-blue-900">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center"><Mail className="text-white" size={20} /></div>
              <div>
                <h2 className="text-lg font-bold text-white">Email Marketing</h2>
                <p className="text-blue-300 text-xs">{usersWithEmail.length} users with email · {sentStats.campaigns} campaigns sent</p>
              </div>
            </div>
            <div className="flex gap-1 bg-white/10 rounded-xl p-1">
              {[{ id: 'compose', icon: Edit3, label: 'Compose' }, { id: 'drafts', icon: Inbox, label: `Drafts${drafts.length ? ` (${drafts.length})` : ''}` }, { id: 'sent', icon: Send, label: 'Sent' }, { id: 'analytics', icon: BarChart, label: 'Analytics' }].map(tab => (
                <button key={tab.id} onClick={() => setView(tab.id)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${view === tab.id ? 'bg-white text-slate-900' : 'text-blue-200 hover:bg-white/10'}`}>
                  <tab.icon size={13} />{tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* COMPOSE */}
        {view === 'compose' && (
          <div className="p-6">
            {!previewMode ? (
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Left: form */}
                <div className="lg:col-span-2 space-y-5">
                  {/* Templates */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2"><Tag size={12} className="inline mr-1" />Quick Templates</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {EMAIL_TEMPLATES.map(t => (
                        <button key={t.id} onClick={() => applyTemplate(t.id)} className={`text-left px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all ${selectedTemplate === t.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-blue-300 hover:bg-blue-50'}`}>{t.label}</button>
                      ))}
                    </div>
                  </div>
                  {/* Subject */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Subject Line *</label>
                    <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Enter email subject..."
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm text-slate-900" />
                  </div>
                  {/* Body */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Message Body *</label>
                      <span className="text-xs text-slate-400">Use <code className="bg-slate-100 px-1 rounded">{'{{name}}'}</code> to personalise</span>
                    </div>
                    <textarea value={body} onChange={e => setBody(e.target.value)} rows={12} placeholder="Write your email message here..."
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm text-slate-900 resize-y font-mono" />
                    <p className="text-xs text-slate-400 mt-1">{body.length} characters</p>
                  </div>
                  {/* Action buttons */}
                  <div className="flex gap-3 flex-wrap">
                    <button onClick={handleSend} disabled={sending} className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition disabled:opacity-50">
                      {sending ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Sending...</> : <><Send size={16} />Send to {finalRecipients.length} Recipient{finalRecipients.length !== 1 ? 's' : ''}</>}
                    </button>
                    <button onClick={() => setPreviewMode(true)} className="flex items-center gap-2 px-4 py-3 border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition text-sm"><Eye size={16} />Preview</button>
                    <button onClick={saveDraft} className="flex items-center gap-2 px-4 py-3 border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition text-sm"><Inbox size={16} />Save Draft</button>
                    <button onClick={() => { setSubject(''); setBody(''); setSelectedTemplate(''); }} className="flex items-center gap-2 px-4 py-3 border border-red-100 text-red-500 font-semibold rounded-xl hover:bg-red-50 transition text-sm"><Trash2 size={16} />Clear</button>
                  </div>
                </div>

                {/* Right: recipients */}
                <div className="space-y-4">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider"><Users size={12} className="inline mr-1" />Recipients</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ id: 'all', label: 'All Users', icon: Users }, { id: 'segment', label: 'Segment', icon: Filter }, { id: 'individual', label: 'Individual', icon: UserIcon }, { id: 'custom', label: 'Custom List', icon: Edit3 }].map(m => (
                      <button key={m.id} onClick={() => setRecipientMode(m.id)} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all ${recipientMode === m.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'}`}>
                        <m.icon size={13} />{m.label}
                      </button>
                    ))}
                  </div>

                  {recipientMode === 'all' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                      <Users size={24} className="text-blue-500 mx-auto mb-2" />
                      <p className="font-bold text-blue-800 text-2xl">{usersWithEmail.length}</p>
                      <p className="text-xs text-blue-600">users with email addresses</p>
                    </div>
                  )}

                  {recipientMode === 'segment' && (
                    <div className="space-y-2">
                      {SEGMENTS.map(seg => (
                        <button key={seg.id} onClick={() => setSegment(seg.id)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-all ${segment === seg.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'}`}>
                          <span className={`font-medium ${segment === seg.id ? 'text-blue-700' : 'text-slate-700'}`}>{seg.label}</span>
                          <span className={`font-bold text-xs px-2 py-0.5 rounded-full ${segment === seg.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{seg.count}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {recipientMode === 'individual' && (
                    <div>
                      <div className="relative mb-3"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} /><input type="text" placeholder="Search users..." value={recipientSearch} onChange={e => setRecipientSearch(e.target.value)} className="pl-8 pr-4 py-2 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-xs w-full" /></div>
                      <div className="border border-slate-200 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                          <span className="text-xs text-slate-500">{selectedRecipients.length} selected</span>
                          <div className="flex gap-2"><button onClick={() => setSelectedRecipients(filteredUserList.map(u => u.telegramName))} className="text-xs text-blue-600 font-medium">All</button><button onClick={() => setSelectedRecipients([])} className="text-xs text-slate-500 font-medium">None</button></div>
                        </div>
                        {filteredUserList.slice(0, 30).map(u => (
                          <label key={u.telegramName} className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors border-b border-slate-50 last:border-0 ${selectedRecipients.includes(u.telegramName) ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                            <input type="checkbox" checked={selectedRecipients.includes(u.telegramName)} onChange={() => toggleRecipient(u.telegramName)} className="rounded border-slate-300 text-blue-600" />
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{u.telegramName.charAt(0).toUpperCase()}</div>
                            <div className="min-w-0"><p className="text-xs font-medium text-slate-800 truncate">@{u.telegramName}</p><p className="text-xs text-slate-400 truncate">{u.email}</p></div>
                          </label>
                        ))}
                        {filteredUserList.length === 0 && <p className="text-center text-xs text-slate-400 py-6">No users found</p>}
                      </div>
                    </div>
                  )}

                  {recipientMode === 'custom' && (
                    <div>
                      <textarea value={customEmails} onChange={e => setCustomEmails(e.target.value)} rows={6}
                        placeholder={"Enter emails separated by commas or new lines:\n\nuser1@email.com\nuser2@email.com"}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-xs text-slate-900 resize-y font-mono" />
                      <p className="text-xs text-slate-400 mt-1">{customEmails.split(/[\n,]/).map(e => e.trim()).filter(Boolean).length} addresses entered</p>
                    </div>
                  )}

                  <div className={`flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm ${finalRecipients.length > 0 ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>
                    <Send size={15} />{finalRecipients.length} recipient{finalRecipients.length !== 1 ? 's' : ''} selected
                  </div>
                </div>
              </div>
            ) : (
              /* Preview */
              <div className="max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2"><Eye size={18} />Email Preview</h3>
                  <button onClick={() => setPreviewMode(false)} className="text-sm text-blue-600 font-semibold hover:underline">Back to Editor</button>
                </div>
                <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="bg-slate-100 px-5 py-3 border-b border-slate-200 space-y-1 text-xs text-slate-500">
                    <p><span className="font-semibold text-slate-700">From:</span> MODANMIC &lt;noreply@modanmic.com&gt;</p>
                    <p><span className="font-semibold text-slate-700">To:</span> {finalRecipients.length} recipient{finalRecipients.length !== 1 ? 's' : ''}</p>
                    <p><span className="font-semibold text-slate-700">Subject:</span> {previewSubject || '(No subject)'}</p>
                  </div>
                  <div className="bg-white p-8">
                    <div className="mb-6 pb-4 border-b border-slate-100 flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center"><span className="text-white font-black text-sm">M</span></div>
                      <span className="font-black text-slate-900 text-lg">MODANMIC</span>
                    </div>
                    <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">{previewBody || '(No message)'}</pre>
                    <div className="mt-8 pt-4 border-t border-slate-100 text-xs text-slate-400 text-center">
                      <p>{new Date().getFullYear()} MODANMIC · You received this because you are a registered user.</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button onClick={handleSend} disabled={sending} className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition disabled:opacity-50">
                    {sending ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Sending...</> : <><Send size={16} />Confirm and Send</>}
                  </button>
                  <button onClick={() => setPreviewMode(false)} className="px-4 py-3 border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition text-sm">Edit</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* DRAFTS */}
        {view === 'drafts' && (
          <div className="p-6">
            {drafts.length === 0 ? (
              <div className="text-center py-16"><Inbox size={40} className="mx-auto text-slate-300 mb-3" /><p className="text-slate-400 font-medium">No drafts saved yet</p><button onClick={() => setView('compose')} className="mt-4 text-blue-600 text-sm font-semibold hover:underline">Start composing</button></div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-500 mb-4">{drafts.length} saved draft{drafts.length !== 1 ? 's' : ''}</p>
                {drafts.map(draft => (
                  <div key={draft.id} className="border border-slate-200 rounded-xl p-4 flex items-start justify-between gap-4 hover:bg-slate-50 transition">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 text-sm truncate">{draft.subject}</p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{draft.body?.substring(0, 100)}...</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={11} />{new Date(draft.savedAt).toLocaleString()}</span>
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium capitalize">{draft.recipientMode}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => loadDraftIntoComposer(draft)} className="text-xs px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-semibold transition flex items-center gap-1"><Edit3 size={11} />Edit</button>
                      <button onClick={() => deleteDraft(draft.id)} className="text-xs px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 font-semibold transition flex items-center gap-1"><Trash2 size={11} />Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SENT */}
        {view === 'sent' && (
          <div className="p-6">
            {sent.length === 0 ? (
              <div className="text-center py-16"><Send size={40} className="mx-auto text-slate-300 mb-3" /><p className="text-slate-400 font-medium">No emails sent yet</p><button onClick={() => setView('compose')} className="mt-4 text-blue-600 text-sm font-semibold hover:underline">Send your first campaign</button></div>
            ) : (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center"><p className="text-2xl font-black text-blue-700">{sentStats.campaigns}</p><p className="text-xs font-semibold text-blue-600 mt-0.5">Total Campaigns</p></div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center"><p className="text-2xl font-black text-green-700">{sentStats.totalSent.toLocaleString()}</p><p className="text-xs font-semibold text-green-600 mt-0.5">Emails Delivered</p></div>
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center"><p className="text-2xl font-black text-purple-700">{usersWithEmail.length}</p><p className="text-xs font-semibold text-purple-600 mt-0.5">Addressable Users</p></div>
                </div>
                <div className="space-y-3">
                  {sent.map(record => (
                    <div key={record.id} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">{record.subject}</p>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{record.body?.substring(0, 80)}...</p>
                        </div>
                        <span className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full"><CheckCircle size={11} />Sent</span>
                      </div>
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Users size={11} />{record.recipientCount} recipients</span>
                        <span className="flex items-center gap-1"><Clock size={11} />{new Date(record.sentAt).toLocaleString()}</span>
                        {record.segment && <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full capitalize">{record.segment}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ANALYTICS */}
        {view === 'analytics' && (
          <div className="p-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Send}     label="Campaigns Sent"    value={sentStats.campaigns}                color="blue" />
              <StatCard icon={Users}    label="Total Delivered"   value={sentStats.totalSent.toLocaleString()} color="green" />
              <StatCard icon={Mail}     label="Addressable Users" value={usersWithEmail.length}              color="purple" />
              <StatCard icon={Inbox}    label="Saved Drafts"      value={drafts.length}                      color="amber" />
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-6">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Filter size={16} />Audience Segments</h3>
              <div className="space-y-3">
                {SEGMENTS.map(seg => {
                  const pct = usersWithEmail.length > 0 ? Math.round(seg.count / usersWithEmail.length * 100) : 0;
                  return (
                    <div key={seg.id} className="flex items-center gap-4">
                      <div className="w-36 flex-shrink-0 text-sm font-medium text-slate-700">{seg.label}</div>
                      <div className="flex-1 bg-slate-200 rounded-full h-2.5 overflow-hidden"><div className="bg-blue-500 h-2.5 rounded-full" style={{ width: `${pct}%` }} /></div>
                      <div className="w-20 text-right text-sm font-bold text-slate-800">{seg.count} <span className="font-normal text-slate-400 text-xs">({pct}%)</span></div>
                      <button onClick={() => { setRecipientMode('segment'); setSegment(seg.id); setView('compose'); }} className="text-xs text-blue-600 hover:underline font-semibold flex-shrink-0">Target</button>
                    </div>
                  );
                })}
              </div>
            </div>
            {sent.length > 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 bg-slate-50"><h3 className="font-bold text-slate-800 flex items-center gap-2"><BarChart size={16} />Campaign History</h3></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-100"><th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Subject</th><th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Recipients</th><th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Segment</th><th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Sent</th></tr></thead>
                    <tbody>{sent.map(record => (<tr key={record.id} className="border-b border-slate-50 hover:bg-slate-50 transition"><td className="py-3 px-4 font-medium text-slate-900 max-w-xs truncate">{record.subject}</td><td className="py-3 px-4 text-slate-600">{record.recipientCount.toLocaleString()}</td><td className="py-3 px-4"><span className="bg-slate-100 text-slate-600 text-xs font-medium px-2 py-0.5 rounded-full capitalize">{record.segment || record.recipientMode}</span></td><td className="py-3 px-4 text-slate-400 text-xs">{new Date(record.sentAt).toLocaleString()}</td></tr>))}</tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-200"><BarChart size={36} className="mx-auto text-slate-300 mb-3" /><p className="text-slate-400">No campaigns yet — analytics will appear here after your first send.</p><button onClick={() => setView('compose')} className="mt-4 text-blue-600 text-sm font-semibold hover:underline">Send a campaign</button></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Admin Dashboard Inner ───────────────────────────────────────────────
const AdminDashboardInner = ({ adminEmail, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [stats, setStats]               = useState({ totalUsers: 0, totalBalance: 0, totalPene: 0, verified: 0 });
  const [sortField, setSortField]       = useState('dateCreated');
  const [sortDir, setSortDir]           = useState('desc');
  const [activeTab, setActiveTab]       = useState('users');
  const [emailTargetUser, setEmailTargetUser] = useState(null);
  const [menuOpen, setMenuOpen]         = useState(false);

  const fetchAllUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/users/ADMIN`);
      const data = await res.json();
      const all  = data?.usernameInfo || data?.mainUser || [];
      if (all.length > 0) {
        setUsers(all);
        let tb = 0, tp = 0, tv = 0;
        all.forEach(u => {
          tb += parseFloat((u.accountBalance || '$0').replace(/[^0-9.]/g, '')) || 0;
          tp += parseFloat(u.investmentBalance || 0) || 0;
          if (u.validated === 'verified' || u.validated === 'true') tv++;
        });
        setStats({ totalUsers: all.length, totalBalance: tb, totalPene: tp, verified: tv });
      }
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAllUsers(); }, [fetchAllUsers]);

  const filtered = users.filter(u =>
    (u.telegramName || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortField] || '', bv = b[sortField] || '';
    if (sortField === 'accountBalance') { av = parseFloat((av || '$0').replace(/[^0-9.]/g, '')) || 0; bv = parseFloat((bv || '$0').replace(/[^0-9.]/g, '')) || 0; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => { if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(field); setSortDir('desc'); } };
  const SortIcon = ({ field }) => sortField === field ? (sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />) : null;

  const usersWithEmail = users.filter(u => u.email).length;

  const handleEmailUser = (user) => {
    setEmailTargetUser(user.telegramName);
    setSelectedUser(null);
    setActiveTab('email');
  };

  const NAV_TABS   = [
    { id: 'users',    icon: Users,    label: 'Users' },
    { id: 'features', icon: Sliders,  label: 'Features' },
    { id: 'email',    icon: Mail,     label: 'Email Marketing' },
  ];

  const handleTabChange = (id) => { setActiveTab(id); setMenuOpen(false); };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Top Nav ── */}
      <nav className="bg-slate-950 border-b border-slate-800 px-4 sm:px-6 py-4 sticky top-0 z-30">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-red-700 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="text-white" size={18} />
            </div>
            <div>
              <h1 className="text-white font-black text-base leading-tight">MODANMIC Admin</h1>
              <p className="text-slate-500 text-xs hidden sm:block">{adminEmail}</p>
            </div>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_TABS.map(tab => (
              <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl transition text-sm font-semibold ${activeTab === tab.id ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                <tab.icon size={15} />{tab.label}
              </button>
            ))}
            <div className="w-px h-6 bg-slate-700 mx-1" />
            <button onClick={fetchAllUsers} className="flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition text-sm">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />Refresh
            </button>
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition text-sm">
              <Activity size={15} />Live App
            </button>
            <button onClick={onLogout} className="flex items-center gap-2 px-3 py-2 bg-red-900/40 text-red-300 hover:bg-red-900/70 rounded-xl transition text-sm font-semibold">
              <LogOut size={15} />Logout
            </button>
          </div>

          {/* Mobile: active tab label + burger */}
          <div className="flex md:hidden items-center gap-3">
            <span className="text-slate-400 text-xs font-semibold capitalize">{NAV_TABS.find(t => t.id === activeTab)?.label}</span>
            <button onClick={() => setMenuOpen(o => !o)}
              className="w-10 h-10 flex flex-col items-center justify-center gap-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 transition"
              aria-label="Toggle menu">
              <span className={`block w-5 h-0.5 bg-white rounded-full transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block w-5 h-0.5 bg-white rounded-full transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-5 h-0.5 bg-white rounded-full transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <div className="md:hidden mt-4 pb-2 border-t border-slate-800 pt-4 space-y-1">
            {NAV_TABS.map(tab => (
              <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition text-sm font-semibold ${activeTab === tab.id ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                <tab.icon size={16} />{tab.label}
                {activeTab === tab.id && <span className="ml-auto w-2 h-2 rounded-full bg-red-500" />}
              </button>
            ))}
            <div className="h-px bg-slate-800 my-2" />
            <button onClick={() => { fetchAllUsers(); setMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition text-sm font-semibold">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />Refresh Data
            </button>
            <button onClick={() => { navigate('/dashboard'); setMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition text-sm font-semibold">
              <Activity size={16} />View Live App
            </button>
            <button onClick={onLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-900/30 text-red-300 hover:bg-red-900/60 transition text-sm font-semibold">
              <LogOut size={16} />Logout
            </button>
            <p className="text-slate-600 text-xs text-center pt-2">{adminEmail}</p>
          </div>
        )}
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Users}       label="Total Users"       value={stats.totalUsers.toLocaleString()} color="indigo" />
          <StatCard icon={DollarSign}  label="Total Balances"    value={`$${stats.totalBalance.toLocaleString()}`} color="green" />
          <StatCard icon={TrendingUp}  label="Total PENE"        value={stats.totalPene.toLocaleString()} color="purple" />
          <StatCard icon={CheckCircle} label="Verified Accounts" value={stats.verified} sub={`${stats.totalUsers ? Math.round(stats.verified / stats.totalUsers * 100) : 0}% of total`} color="amber" />
          <StatCard icon={Mail}        label="Email Addressable" value={usersWithEmail} sub="users with email" color="blue" />
        </div>

        {activeTab === 'features' && <FeatureTogglePanel users={users} />}
        {activeTab === 'email'    && <EmailMarketingPanel users={users} onTargetUser={emailTargetUser} />}

        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between gap-4 flex-wrap">
              <div><h2 className="text-lg font-bold text-slate-900">All Users</h2><p className="text-sm text-slate-500">{filtered.length} user{filtered.length !== 1 ? 's' : ''} found</p></div>
              <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type="text" placeholder="Search username or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm w-64" /></div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" /></div>
            ) : sorted.length === 0 ? (
              <div className="text-center py-16 text-slate-400"><Users size={40} className="mx-auto mb-3 opacity-30" /><p>No users found</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-800" onClick={() => toggleSort('telegramName')}><span className="flex items-center gap-1">User <SortIcon field="telegramName" /></span></th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-800" onClick={() => toggleSort('accountBalance')}><span className="flex items-center gap-1">Balance <SortIcon field="accountBalance" /></span></th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">PENE</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-800" onClick={() => toggleSort('dateCreated')}><span className="flex items-center gap-1">Joined <SortIcon field="dateCreated" /></span></th>
                      <th className="py-3 px-4" />
                    </tr>
                  </thead>
                  <tbody>{sorted.map((u, i) => <UserRow key={u._id || i} user={u} onView={setSelectedUser} />)}</tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-slate-400 text-xs mt-6">MODANMIC Admin Panel · {adminEmail} · {new Date().toLocaleDateString()}</p>
      </div>

      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-7 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setSelectedUser(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100"><X size={18} /></button>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-black text-2xl">{(selectedUser.telegramName || '?').charAt(0).toUpperCase()}</div>
              <div>
                <h2 className="text-xl font-black text-slate-900">@{selectedUser.telegramName}</h2>
                <p className="text-slate-500 text-sm">{selectedUser.email || 'No email'}</p>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold mt-1 ${selectedUser.validated === 'verified' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                  {selectedUser.validated === 'verified' ? <CheckCircle size={11} /> : <AlertCircle size={11} />}
                  {selectedUser.validated === 'verified' ? 'Verified' : 'Unverified'}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { label: 'Account Balance', value: selectedUser.accountBalance || '$0.00' },
                { label: 'PENE Balance',     value: `${parseFloat(selectedUser.investmentBalance || 0).toLocaleString()} PENE` },
                { label: 'Referral Balance', value: `$${parseFloat(selectedUser.refralBalance || 0).toFixed(2)}` },
                { label: 'Referrals',        value: (selectedUser.refralList || []).length },
                { label: 'Member Since',     value: selectedUser.dateCreated ? new Date(selectedUser.dateCreated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '-' },
                { label: 'Last Deposit',     value: selectedUser.lastDepsit || 'None' },
                { label: 'Full Name',        value: selectedUser.fullName || '-' },
                { label: 'Phone',            value: selectedUser.phone || '-' },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                  <span className="text-sm text-slate-500">{row.label}</span>
                  <span className="text-sm font-semibold text-slate-900">{row.value}</span>
                </div>
              ))}
            </div>
            {selectedUser.email && (
              <button onClick={() => handleEmailUser(selectedUser)} className="mt-5 w-full flex items-center justify-center gap-2 py-2.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl text-sm font-semibold hover:bg-blue-100 transition">
                <Mail size={15} />Send Email to This User
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Root ─────────────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const [adminEmail, setAdminEmail] = useState(() => {
    try {
      const session = JSON.parse(localStorage.getItem('admin_session') || 'null');
      if (!session) return null;
      if (Date.now() - session.ts > 8 * 60 * 60 * 1000) { localStorage.removeItem('admin_session'); return null; }
      if (!ADMIN_EMAILS.includes(session.email)) return null;
      return session.email;
    } catch { return null; }
  });

  const handleLogout = () => { localStorage.removeItem('admin_session'); setAdminEmail(null); toast.success('Logged out from admin panel'); };

  if (!adminEmail) return <AdminLogin onLogin={setAdminEmail} />;
  return <AdminDashboardInner adminEmail={adminEmail} onLogout={handleLogout} />;
};

export default AdminDashboard;
